// Bug #399 (2026-10-09): stale-unit SELF-REPAIR for the stored ACTIVE unit.
//
// Production evidence (complaint #399, STATE_ANOMALY staleUnitBlocked):
// when the id persisted in vecindus_active_owned_unit stopped existing
// server-side, every /home/* request short-circuited in the api.ts request
// interceptor with repaired:false — forever. The existing repair chain in
// AuthContext.refreshMe could not fire because (a) with zero contexts /
// zero condo-linked units there is no `selected` condo to repair from, and
// (b) when the refreshMe units fetch itself fails, NOTHING cleans storage
// and the dead selection survives reloads.
//
// This module is the storage-side repair: purge the VERIFIED-dead id from
// the active-selection key, from every per-condo last-selected memory key,
// and (defensively) from the known-units snapshot. It is pure and storage-
// injected so it can be unit-tested without a browser.
//
// Deliberate limits:
//  - It NEVER invents a fallback unit: the interceptor cannot verify
//    ownership, and serving another home's data is the TH1 leak class. The
//    caller re-boots into clean unit-selection state; refreshMe's verified
//    fallback chain (or the unit picker) takes over from there.
//  - It never touches vecindus_last_condo / vecindus_active_condo — the
//    condo selection stays recoverable (personal-lens dip contract).

export const ACTIVE_OWNED_UNIT_KEY = 'vecindus_active_owned_unit';
export const KNOWN_UNIT_IDS_KEY = 'vecindus_known_unit_ids';
const LAST_UNIT_PREFIX = 'vecindus_last_unit_';

export interface StaleUnitRepairResult {
  /** true when the stored ACTIVE selection WAS the dead id and was cleared. */
  clearedActiveSelection: boolean;
  /** per-condo memory keys purged because they referenced the dead id. */
  clearedMemoryKeys: string[];
  /** true when the dead id was removed from the known-units snapshot. */
  removedFromKnownIds: boolean;
}

type MinimalStorage = Pick<Storage, 'length' | 'key' | 'getItem' | 'setItem' | 'removeItem'>;

export function repairStaleActiveUnit(
  deadUnitId: string,
  storage: MinimalStorage,
): StaleUnitRepairResult {
  const result: StaleUnitRepairResult = {
    clearedActiveSelection: false,
    clearedMemoryKeys: [],
    removedFromKnownIds: false,
  };
  if (!deadUnitId) return result;

  // 1) Drop the dead ACTIVE selection — only when it is actually the dead id
  // (never clobber a selection that refreshMe repaired concurrently).
  try {
    if (storage.getItem(ACTIVE_OWNED_UNIT_KEY) === deadUnitId) {
      storage.removeItem(ACTIVE_OWNED_UNIT_KEY);
      result.clearedActiveSelection = true;
    }
  } catch { /* storage optional */ }

  // 2) Purge the dead id from per-condo last-selected memory keys so the
  // boot-time restore path cannot resurrect it. Normally memory is only
  // overwritten after a VERIFIED selection (Kimi TH1 contract) — but this id
  // is VERIFIED dead (absent from the known-units snapshot), so destroying
  // the entries is the correct exception.
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith(LAST_UNIT_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      try {
        if (storage.getItem(k) === deadUnitId) {
          storage.removeItem(k);
          result.clearedMemoryKeys.push(k);
        }
      } catch { /* per-key isolation */ }
    }
  } catch { /* storage optional */ }

  // 3) Belt-and-braces: drop the dead id from the known-units snapshot if a
  // concurrent refreshMe re-introduced it in a race window.
  try {
    const raw = storage.getItem(KNOWN_UNIT_IDS_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.map(String).includes(deadUnitId)) {
        const next = parsed.filter((id) => String(id) !== deadUnitId);
        storage.setItem(KNOWN_UNIT_IDS_KEY, JSON.stringify(next));
        result.removedFromKnownIds = true;
      }
    }
  } catch { /* storage optional — snapshot stays as-is */ }

  return result;
}
